import { Sports } from '@gin-betting/constants';
import { App, View } from '@gin-betting/core';
import { findLast } from 'lodash';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import type { MatchPartInfo } from 'types';

const TENNIS_HIDDEN_SCORES_TYPES_RESULT_PAGE: App.ScoreTypes[] = [
    App.ScoreTypes.TYPE_GOLDEN_SET,
    App.ScoreTypes.TYPE_GAME_POINTS,
    App.ScoreTypes.TYPE_TIE_BREAK,
];

type ScoreTypesMap = {
    [key in App.MetaTypes]?: App.ScoreTypes[];
};

const footballFilterScoreTypesMap: ScoreTypesMap = {
    [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: [App.ScoreTypes.TYPE_TOTAL],
    [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
    ],
    [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
        App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
    ],
    [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
        App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
        App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA,
    ],
    [App.MetaTypes.TYPE_PERIOD_PENALTIES]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
        App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
        App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA,
        App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA,
    ],
};

const footballOverviewScoreTypesMap: ScoreTypesMap = {
    [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_RED_CARD,
        App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
    ],
    [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_RED_CARD,
        App.ScoreTypes.TYPE_PERIOD_1ST_HALF,
        App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
    ],
    [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_RED_CARD,
        App.ScoreTypes.TYPE_PERIOD_2ND_HALF,
        App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA,
    ],
    [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_RED_CARD,
        App.ScoreTypes.TYPE_PERIOD_1ST_EXTRA,
        App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA,
    ],
    [App.MetaTypes.TYPE_PERIOD_PENALTIES]: [
        App.ScoreTypes.TYPE_TOTAL,
        App.ScoreTypes.TYPE_RED_CARD,
        App.ScoreTypes.TYPE_PERIOD_2ND_EXTRA,
        App.ScoreTypes.TYPE_PERIOD_PENALTIES,
    ],
};

const footballFilter = (
    scores: View.ScoreInfo[],
    matchPartInfo: MatchPartInfo,
    isOverview: boolean
) => {
    const matchPartInfoType = matchPartInfo.type;

    if (!matchPartInfoType) return scores;

    const scoreTypesMap = isOverview
        ? footballOverviewScoreTypesMap
        : footballFilterScoreTypesMap;
    const scoreTypesCollection = scoreTypesMap[matchPartInfoType];

    if (!scoreTypesCollection) return scores;

    return scores.filter(
        ({ type, home, away }) =>
            scoreTypesCollection.includes(type) &&
            (type !== App.ScoreTypes.TYPE_RED_CARD || !!(+home + +away))
    );
};

const iceHockeyFilter = (
    scores: View.ScoreInfo[],
    matchPartInfo: MatchPartInfo,
    isOverview: boolean
) => {
    if (isOverview) {
        return getLastTwoScores({
            scores,
            scoreTypes: App.ScoreTypes.TYPE_PERIOD_REGULAR,
            isOverview,
        });
    }

    switch (true) {
        case matchPartInfo.type === App.MetaTypes.TYPE_REGULAR &&
            matchPartInfo.indexNumber === 1: {
            return scores.filter(s =>
                [App.ScoreTypes.TYPE_TOTAL].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_REGULAR &&
            matchPartInfo.indexNumber === 2: {
            return scores.filter(
                s =>
                    s.type === App.ScoreTypes.TYPE_TOTAL ||
                    (s.type === App.ScoreTypes.TYPE_PERIOD_REGULAR &&
                        s.indexNumber === 1)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_REGULAR &&
            matchPartInfo.indexNumber === 3: {
            return scores.filter(
                s =>
                    s.type === App.ScoreTypes.TYPE_TOTAL ||
                    (s.type === App.ScoreTypes.TYPE_PERIOD_REGULAR &&
                        [1, 2].includes(s.indexNumber))
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_OVERTIME: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_OVERTIME,
                    App.ScoreTypes.TYPE_PERIOD_REGULAR,
                ].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_BULLETS: {
            return scores.filter(s =>
                [
                    App.ScoreTypes.TYPE_TOTAL,
                    App.ScoreTypes.TYPE_PERIOD_REGULAR,
                ].includes(s.type)
            );
        }

        default: {
            return scores;
        }
    }
};

const basketballFilter = (
    scores: View.ScoreInfo[],
    matchPartInfo: MatchPartInfo,
    isOverview: boolean
) => {
    if (isOverview) {
        switch (true) {
            case matchPartInfo.type === App.MetaTypes.TYPE_QUARTER &&
                matchPartInfo.indexNumber === 1: {
                return scores.filter(s =>
                    [App.ScoreTypes.TYPE_TOTAL].includes(s.type)
                );
            }

            case matchPartInfo.type === App.MetaTypes.TYPE_OVERTIME:
            case matchPartInfo.type === App.MetaTypes.TYPE_QUARTER: {
                return getLastTwoScores({
                    scores,
                    scoreTypes: App.ScoreTypes.TYPE_QUARTER,
                    isOverview,
                });
            }

            default: {
                return scores;
            }
        }
    }

    switch (true) {
        case matchPartInfo.type === App.MetaTypes.TYPE_QUARTER &&
            matchPartInfo.indexNumber === 1: {
            return scores.filter(s =>
                [App.ScoreTypes.TYPE_TOTAL].includes(s.type)
            );
        }

        case matchPartInfo.type === App.MetaTypes.TYPE_OVERTIME: {
            const lastOT = findLast(
                scores,
                s => s.type === App.ScoreTypes.TYPE_PERIOD_OVERTIME
            );

            if (lastOT) {
                return scores.filter(
                    s =>
                        !(
                            s.type === App.ScoreTypes.TYPE_PERIOD_OVERTIME &&
                            s.indexNumber !== lastOT.indexNumber
                        )
                );
            }

            return scores;
        }

        default: {
            return scores;
        }
    }
};

const tennisFilter = (
    scores: View.ScoreInfo[],
    { type, indexNumber, status }: MatchPartInfo,
    isOverview: boolean
) => {
    if (isOverview) {
        const TENNIS_LIMIT_MAP = 3 as const;

        const isTbForCurrentSetExists = scores.find(
            s =>
                s.type === App.ScoreTypes.TYPE_TIE_BREAK &&
                s.indexNumber === indexNumber
        );

        const filteredScores = scores
            .filter(s => s.type !== App.ScoreTypes.TYPE_TIE_BREAK)
            .map(s => {
                if (s.indexNumber !== indexNumber || !isTbForCurrentSetExists) {
                    return s;
                }

                return { ...s, subScore: isTbForCurrentSetExists };
            });

        return scores.length > TENNIS_LIMIT_MAP
            ? scores.slice(0, 1).concat(filteredScores.slice(-TENNIS_LIMIT_MAP))
            : scores;
    }

    const filteredScores = LIVE_SPORTEVENT_STATUSES.includes(status)
        ? scores
        : scores.filter(
              s => !TENNIS_HIDDEN_SCORES_TYPES_RESULT_PAGE.includes(s.type)
          );

    switch (true) {
        case type === App.MetaTypes.TYPE_SET: {
            const isTbForCurrentSetExists = filteredScores.find(
                s =>
                    s.type === App.ScoreTypes.TYPE_TIE_BREAK &&
                    s.indexNumber === indexNumber
            );

            return filteredScores.filter(s => {
                return !(
                    (s.type === App.ScoreTypes.TYPE_TIE_BREAK &&
                        s.indexNumber !== indexNumber) ||
                    (s.type === App.ScoreTypes.TYPE_GAME_POINTS &&
                        isTbForCurrentSetExists)
                );
            });
        }

        default: {
            return filteredScores;
        }
    }
};

const filterScoresBySportAndMatchType = (
    sportId: string,
    matchPartInfo: MatchPartInfo,
    scores: View.ScoreInfo[],
    isOverview: boolean
): View.ScoreInfo[] => {
    if (!matchPartInfo.type || !matchPartInfo.indexNumber) {
        return scores;
    }

    switch (sportId) {
        case Sports.FOOTBALL:
        case Sports.ESPORTS_FIFA:
            return footballFilter(scores, matchPartInfo, isOverview);

        case Sports.ICE_HOCKEY:
            return iceHockeyFilter(scores, matchPartInfo, isOverview);

        case Sports.BASKETBALL:
            return basketballFilter(scores, matchPartInfo, isOverview);

        case Sports.TENNIS:
        case Sports.ESPORTS_TENNIS:
            return tennisFilter(scores, matchPartInfo, isOverview);

        case Sports.ESPORTS_COUNTER_STRIKE:
        case Sports.ESPORTS_DOTA_2:
            return getLastTwoScores({
                scores,
                scoreTypes: App.ScoreTypes.TYPE_MAP,
                isOverview,
            });

        default: {
            return scores;
        }
    }
};

export default filterScoresBySportAndMatchType;

function getLastTwoScores(scoreInfo: {
    scores: View.ScoreInfo[];
    scoreTypes: App.ScoreTypes;
    isOverview: boolean;
}): View.ScoreInfo[] {
    const { scores, scoreTypes, isOverview } = scoreInfo;

    if (!isOverview) return scores;

    const NUMBER_LAST_SCORES = 2 as const;
    const lengthIncomeType = scores.filter(el => el.type === scoreTypes).length;

    if (lengthIncomeType > NUMBER_LAST_SCORES) {
        const filterScores = scores
            .slice(0, 1)
            .concat(scores.slice(-NUMBER_LAST_SCORES));

        return filterScores;
    }

    return scores;
}
